import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { X, Save, Package } from 'lucide-react';
import { fetchTiposCaja, selectActiveTiposCaja } from '../../store/slices/tipoCajaSlice';
import './ProductModal.css';

const EditProductModal = ({ product, onClose, onSave }) => {
  const dispatch = useDispatch();
  const tiposCaja = useSelector(selectActiveTiposCaja);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Cargar tipos de caja al montar el componente
  useEffect(() => {
    dispatch(fetchTiposCaja({ activo: true }));
  }, [dispatch]);

  useEffect(() => {
    // Initialize form data with product values
    setFormData({
      category: product.category || '',
      code: product.code || '',
      description: product.description || '',
      nombre: product.nombre || '',
      weigh_bunches_gr: product.weigh_bunches_gr || '',
      large_cm: product.large_cm || '',
      stems_bunches: product.stems_bunches || '',
      bunches_box: product.bunches_box || '',
      tipo_caja_id: product.tipo_caja_id || '',
      activo: product.activo !== false,
      largo_cm: product.largo_cm || '',
      ancho_cm: product.ancho_cm || '',
      alto_cm: product.alto_cm || '',
      volumen_m3: product.volumen_m3 || '',
      largo_in: product.largo_in || '',
      ancho_in: product.ancho_in || '',
      alto_in: product.alto_in || '',
      volumen_in3: product.volumen_in3 || '',
      peso_neto_ruscus_kg: product.peso_neto_ruscus_kg || '',
      peso_caja_kg: product.peso_caja_kg || '',
      peso_total_caja_kg: product.peso_total_caja_kg || '',
      precio_unitario: product.precio_unitario || '',
      precio_fob_mia: product.precio_fob_mia || '',
      costo: product.costo || ''
    });
  }, [product]);

  // Calcular dimensiones automáticamente cuando cambian los valores en cm
  useEffect(() => {
    const largo = parseFloat(formData.largo_cm) || 0;
    const ancho = parseFloat(formData.ancho_cm) || 0;
    const alto = parseFloat(formData.alto_cm) || 0;

    if (largo > 0 && ancho > 0 && alto > 0) {
      // Calcular volumen en m³ (convertir cm³ a m³)
      const volumen_m3 = (largo * ancho * alto) / 1000000;
      
      // Convertir cm a pulgadas (1 cm = 0.393701 in)
      const CM_TO_INCHES = 0.393701;
      const largo_in = largo * CM_TO_INCHES;
      const ancho_in = ancho * CM_TO_INCHES;
      const alto_in = alto * CM_TO_INCHES;
      
      // Calcular volumen en in³
      const volumen_in3 = largo_in * ancho_in * alto_in;

      setFormData(prev => ({
        ...prev,
        volumen_m3: volumen_m3.toFixed(6),
        largo_in: largo_in.toFixed(2),
        ancho_in: ancho_in.toFixed(2),
        alto_in: alto_in.toFixed(2),
        volumen_in3: volumen_in3.toFixed(2)
      }));
    } else {
      // Si alguna dimensión es 0 o vacía, limpiar los campos calculados
      setFormData(prev => ({
        ...prev,
        volumen_m3: '',
        largo_in: '',
        ancho_in: '',
        alto_in: '',
        volumen_in3: ''
      }));
    }
  }, [formData.largo_cm, formData.ancho_cm, formData.alto_cm]);

  // Calcular peso total automáticamente cuando cambian los pesos
  useEffect(() => {
    const pesoNeto = parseFloat(formData.peso_neto_ruscus_kg) || 0;
    const pesoCaja = parseFloat(formData.peso_caja_kg) || 0;

    if (pesoNeto > 0 || pesoCaja > 0) {
      const pesoTotal = pesoNeto + pesoCaja;
      setFormData(prev => ({
        ...prev,
        peso_total_caja_kg: pesoTotal.toFixed(2)
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        peso_total_caja_kg: ''
      }));
    }
  }, [formData.peso_neto_ruscus_kg, formData.peso_caja_kg]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    // Manejar conversión de booleano para campo activo
    const finalValue = name === 'activo' ? value === 'true' : value;
    setFormData(prev => ({
      ...prev,
      [name]: finalValue
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Required fields
    if (!formData.nombre?.trim()) {
      newErrors.nombre = 'El nombre es requerido';
    }
    if (!formData.code?.trim()) {
      newErrors.code = 'El código es requerido';
    }
    if (!formData.category?.trim()) {
      newErrors.category = 'La categoría es requerida';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      // Convert numeric fields
      let productData = {
        ...formData,
        weigh_bunches_gr: formData.weigh_bunches_gr ? parseInt(formData.weigh_bunches_gr) : null,
        stems_bunches: formData.stems_bunches ? parseInt(formData.stems_bunches) : null,
        bunches_box: formData.bunches_box ? parseInt(formData.bunches_box) : null,
        largo_cm: formData.largo_cm ? parseInt(formData.largo_cm) : null,
        ancho_cm: formData.ancho_cm ? parseInt(formData.ancho_cm) : null,
        alto_cm: formData.alto_cm ? parseInt(formData.alto_cm) : null,
        volumen_m3: formData.volumen_m3 ? parseFloat(formData.volumen_m3) : null,
        largo_in: formData.largo_in ? parseFloat(formData.largo_in) : null,
        ancho_in: formData.ancho_in ? parseFloat(formData.ancho_in) : null,
        alto_in: formData.alto_in ? parseFloat(formData.alto_in) : null,
        volumen_in3: formData.volumen_in3 ? parseFloat(formData.volumen_in3) : null,
        peso_neto_ruscus_kg: formData.peso_neto_ruscus_kg ? parseFloat(formData.peso_neto_ruscus_kg) : null,
        peso_caja_kg: formData.peso_caja_kg ? parseFloat(formData.peso_caja_kg) : null,
        peso_total_caja_kg: formData.peso_total_caja_kg ? parseFloat(formData.peso_total_caja_kg) : null,
        precio_unitario: formData.precio_unitario ? parseFloat(formData.precio_unitario) : null,
        precio_fob_mia: formData.precio_fob_mia ? parseFloat(formData.precio_fob_mia) : null,
        costo: formData.costo ? parseFloat(formData.costo) : null
      };

      // Apply Despacho category constraints
      if (productData.category === 'Despacho') {
        productData = {
          ...productData,
          // Set all physical attributes to 0
          weigh_bunches_gr: 0,
          large_cm: '0',
          largo_cm: 0,
          ancho_cm: 0,
          alto_cm: 0,
          volumen_m3: 0,
          largo_in: 0,
          ancho_in: 0,
          alto_in: 0,
          volumen_in3: 0,
          peso_neto_ruscus_kg: 0,
          peso_caja_kg: 0,
          peso_total_caja_kg: 0,
          // Enforce pricing consistency
          bunches_box: 1,
          stems_bunches: 1,
          // No box type association
          tipo_caja_id: null,
          tipo_de_caja: null
        };
      }

      await onSave(product.id, productData);
      onClose();
    } catch (error) {
      console.error('Error updating product:', error);
      alert('Error al actualizar el producto');
    } finally {
      setLoading(false);
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="modal-backdrop" onClick={handleBackdropClick}>
      <div className="modal-content product-modal">
        {/* Header */}
        <div className="modal-header">
          <div>
            <h2 className="modal-title">
              <Package size={24} className="inline-icon" />
              Editar Producto
            </h2>
            <p className="modal-subtitle">{product.nombre}</p>
            <span className="product-code-badge">{product.code}</span>
          </div>
          <button 
            className="btn-icon" 
            onClick={onClose}
            type="button"
          >
            <X size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {/* Información Básica */}
            <div className="form-section">
              <h3 className="section-title">Información Básica</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Nombre *</label>
                  <input
                    type="text"
                    name="nombre"
                    className={`form-input ${errors.nombre ? 'error' : ''}`}
                    value={formData.nombre}
                    onChange={handleChange}
                    placeholder="Ej: Ruscus 50cm"
                  />
                  {errors.nombre && <span className="error-message">{errors.nombre}</span>}
                </div>

                <div className="form-group">
                  <label className="form-label">Código *</label>
                  <input
                    type="text"
                    name="code"
                    className={`form-input ${errors.code ? 'error' : ''}`}
                    value={formData.code}
                    onChange={handleChange}
                    placeholder="Ej: 50cm50b5s250g"
                  />
                  {errors.code && <span className="error-message">{errors.code}</span>}
                </div>

                <div className="form-group">
                  <label className="form-label">Categoría *</label>
                  <select
                    name="category"
                    className={`form-input ${errors.category ? 'error' : ''}`}
                    value={formData.category}
                    onChange={handleChange}
                  >
                    <option value="">Seleccione categoría</option>
                    <option value="Ruscus">Ruscus</option>
                    <option value="Despacho">Despacho</option>
                  </select>
                  {errors.category && <span className="error-message">{errors.category}</span>}
                </div>

                <div className="form-group">
                  <label className="form-label">Descripción</label>
                  <input
                    type="text"
                    name="description"
                    className="form-input"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Descripción del producto"
                  />
                </div>
              </div>
            </div>

            {/* Especificaciones */}
            {formData.category !== 'Despacho' && (
            <div className="form-section">
              <h3 className="section-title">Especificaciones</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Largo (cm)</label>
                  <input
                    type="text"
                    name="large_cm"
                    className="form-input"
                    value={formData.large_cm}
                    onChange={handleChange}
                    placeholder="Ej: 50"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Peso Ramo (gr)</label>
                  <input
                    type="number"
                    name="weigh_bunches_gr"
                    className="form-input"
                    value={formData.weigh_bunches_gr}
                    onChange={handleChange}
                    placeholder="Ej: 250"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Tallos por Ramo</label>
                  <input
                    type="number"
                    name="stems_bunches"
                    className="form-input"
                    value={formData.stems_bunches}
                    onChange={handleChange}
                    placeholder="Ej: 5"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Ramos por Caja</label>
                  <input
                    type="number"
                    name="bunches_box"
                    className="form-input"
                    value={formData.bunches_box}
                    onChange={handleChange}
                    placeholder="Ej: 50"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Tipo de Caja</label>
                  <select
                    name="tipo_caja_id"
                    className="form-input"
                    value={formData.tipo_caja_id}
                    onChange={handleChange}
                  >
                    <option value="">Seleccione tipo</option>
                    {tiposCaja.map((tipo) => (
                      <option key={tipo.id} value={tipo.id}>
                        {tipo.tipo} - {tipo.descripcion}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Estado</label>
                  <select
                    name="activo"
                    className="form-input"
                    value={formData.activo ? 'true' : 'false'}
                    onChange={handleChange}
                  >
                    <option value="true">Activo</option>
                    <option value="false">Inactivo</option>
                  </select>
                </div>
              </div>
            </div>
            )}

            {/* Dimensiones de Caja (cm) */}
            {formData.category !== 'Despacho' && (
            <div className="form-section">
              <h3 className="section-title">Dimensiones de Caja (cm)</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Largo (cm)</label>
                  <input
                    type="number"
                    name="largo_cm"
                    className="form-input"
                    value={formData.largo_cm}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Ancho (cm)</label>
                  <input
                    type="number"
                    name="ancho_cm"
                    className="form-input"
                    value={formData.ancho_cm}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Alto (cm)</label>
                  <input
                    type="number"
                    name="alto_cm"
                    className="form-input"
                    value={formData.alto_cm}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Volumen (m³)</label>
                  <input
                    type="number"
                    step="0.01"
                    name="volumen_m3"
                    className="form-input"
                    value={formData.volumen_m3}
                    onChange={handleChange}
                    disabled
                    style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                  />
                </div>
              </div>
            </div>
            )}

            {/* Dimensiones de Caja (in) */}
            {formData.category !== 'Despacho' && (
            <div className="form-section">
              <h3 className="section-title">Dimensiones de Caja (in)</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Largo (in)</label>
                  <input
                    type="number"
                    step="0.01"
                    name="largo_in"
                    className="form-input"
                    value={formData.largo_in}
                    onChange={handleChange}
                    disabled
                    style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Ancho (in)</label>
                  <input
                    type="number"
                    step="0.01"
                    name="ancho_in"
                    className="form-input"
                    value={formData.ancho_in}
                    onChange={handleChange}
                    disabled
                    style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Alto (in)</label>
                  <input
                    type="number"
                    step="0.01"
                    name="alto_in"
                    className="form-input"
                    value={formData.alto_in}
                    onChange={handleChange}
                    disabled
                    style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Volumen (in³)</label>
                  <input
                    type="number"
                    step="0.01"
                    name="volumen_in3"
                    className="form-input"
                    value={formData.volumen_in3}
                    onChange={handleChange}
                    disabled
                    style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                  />
                </div>
              </div>
            </div>
            )}

            {/* Pesos */}
            {formData.category !== 'Despacho' && (
            <div className="form-section">
              <h3 className="section-title">Pesos</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Peso Neto Ruscus (kg)</label>
                  <input
                    type="number"
                    step="0.01"
                    name="peso_neto_ruscus_kg"
                    className="form-input"
                    value={formData.peso_neto_ruscus_kg}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Peso Caja (kg)</label>
                  <input
                    type="number"
                    step="0.01"
                    name="peso_caja_kg"
                    className="form-input"
                    value={formData.peso_caja_kg}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Peso Total Caja (kg)</label>
                  <input
                    type="number"
                    step="0.01"
                    name="peso_total_caja_kg"
                    className="form-input"
                    value={formData.peso_total_caja_kg}
                    onChange={handleChange}
                    disabled
                    style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                  />
                </div>
              </div>
            </div>
            )}

            {/* Precios */}
            <div className="form-section">
              <h3 className="section-title">Precios</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Precio Nacional (CLP)</label>
                  <input
                    type="number"
                    step="0.01"
                    name="precio_unitario"
                    className="form-input"
                    value={formData.precio_unitario}
                    onChange={handleChange}
                    placeholder="Ej: 15000"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Precio FOB MIA (USD)</label>
                  <input
                    type="number"
                    step="0.01"
                    name="precio_fob_mia"
                    className="form-input"
                    value={formData.precio_fob_mia}
                    onChange={handleChange}
                    placeholder="Ej: 525.00"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Costo USD</label>
                  <input
                    type="number"
                    step="0.01"
                    name="costo"
                    className="form-input"
                    value={formData.costo}
                    onChange={handleChange}
                    placeholder="Ej: 367.50"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-outline"
              onClick={onClose}
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              <Save size={18} />
              {loading ? 'Actualizando...' : 'Actualizar Producto'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditProductModal;
